import { useMutation, useQuery } from '@tanstack/react-query';

import { axiosClient } from '@/api/axiosClient';
import { useCallApi } from '@/hooks/useCallApi';
import { MutationOptions, PaginationResponse, QueryOptions } from '@/models';
import { FactoryModel } from '@/modules/factory/models';
import { fakeNetworkResponse } from '@/utils';

const BASE = '/factory';

// V1.4 A1 — list / filter factories. customer_id filter scopes to one
// Customer's sites; otherwise returns all.
export function useGetFactoryList(
  params: { customer_id?: string; query?: string; page?: number; limit?: number },
  options?: QueryOptions<
    PaginationResponse<FactoryModel[]>,
    unknown,
    PaginationResponse<FactoryModel[]>,
    (string | number | undefined)[]
  >
) {
  const { handleCallApi } = useCallApi();
  const qs = new URLSearchParams();
  if (params.customer_id) qs.set('customer_id', params.customer_id);
  if (params.query) qs.set('query', params.query);
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', String(params.limit ?? 20));

  function handler() {
    return handleCallApi(() =>
      axiosClient.get<PaginationResponse<FactoryModel[]>>(`${BASE}/filter?${qs.toString()}`)
    );
  }

  return useQuery(
    ['factory-list', params.customer_id, params.query, params.page, params.limit],
    handler,
    { cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false, ...options }
  );
}

export function useGetFactoryDetail(id?: string) {
  const { handleCallApi } = useCallApi();

  return useQuery(
    ['factory-detail', id],
    () => handleCallApi(() => axiosClient.get<FactoryModel>(`${BASE}/${id}`)),
    { enabled: !!id, refetchOnWindowFocus: false }
  );
}

export function useCreateFactory(options?: MutationOptions<FactoryModel, FactoryModel>) {
  const { handleCallApi } = useCallApi();

  return useMutation(
    (form: FactoryModel) =>
      handleCallApi(
        async () => {
          const res = await axiosClient.post<FactoryModel>(`${BASE}/create`, form);

          return fakeNetworkResponse(res as unknown as FactoryModel);
        },
        true,
        'Tạo nhà máy thành công. Chiến dịch BÙ đã tự động khởi tạo.'
      ),
    options
  );
}

export function useUpdateFactory(options?: MutationOptions<boolean, FactoryModel>) {
  const { handleCallApi } = useCallApi();

  return useMutation(
    (form: FactoryModel) =>
      handleCallApi(
        async () => {
          await axiosClient.patch(`${BASE}/update`, form);

          return fakeNetworkResponse(true);
        },
        true,
        'Cập nhật nhà máy thành công'
      ),
    options
  );
}

export interface FactoryManagerRow {
  user_id: string;
  name: string;
  phone: string;
  email: string;
  assigned_at?: string;
  assigned_by?: string;
}

// V1.4 D1.4 — persistent QLĐ list backed by GET /factory/:id/managers.
// Unblocks AssignManagerModal showing assignments across modal open/close
// cycles (previously only session-local).
export function useGetFactoryManagers(
  factoryId: string | undefined,
  options?: QueryOptions<FactoryManagerRow[], unknown, FactoryManagerRow[], (string | undefined)[]>
) {
  const { handleCallApi } = useCallApi();

  return useQuery(
    ['factory-managers', factoryId],
    () =>
      handleCallApi(() =>
        axiosClient.get<FactoryManagerRow[]>(`${BASE}/${factoryId}/managers`)
      ),
    { enabled: !!factoryId, refetchOnWindowFocus: false, ...options }
  );
}

export function useAssignFactoryManager(
  options?: MutationOptions<boolean, { factory_id: string; user_id: string }>
) {
  const { handleCallApi } = useCallApi();

  return useMutation(
    ({ factory_id, user_id }) =>
      handleCallApi(
        async () => {
          await axiosClient.post(`${BASE}/${factory_id}/assign-manager`, { user_id });

          return fakeNetworkResponse(true);
        },
        true,
        'Gán QLĐ thành công'
      ),
    options
  );
}

export function useUnassignFactoryManager(
  options?: MutationOptions<boolean, { factory_id: string; user_id: string }>
) {
  const { handleCallApi } = useCallApi();

  return useMutation(
    ({ factory_id, user_id }) =>
      handleCallApi(
        async () => {
          await axiosClient.delete(`${BASE}/${factory_id}/unassign-manager/${user_id}`);

          return fakeNetworkResponse(true);
        },
        true,
        'Bỏ gán QLĐ thành công'
      ),
    options
  );
}
